import * as React from "react"
import { useQuery, useMutation } from "react-query"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { HiOutlinePlus } from "react-icons/hi"
import { RiInformationLine } from "react-icons/ri"
import { BiTrash } from "react-icons/bi"

import { fetcher } from "../lib/fetcher"
import {
  useDisclosure,
  useTheme,
  useToast,
  Flex,
  Heading,
  Button,
  Box,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Popover,
  PopoverTrigger,
  IconButton,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
} from "@chakra-ui/react"
import { ClientAppEntity } from "../lib/types/clientApp"
import { TABLE_PAGE_SIZE } from "../lib/config"
import { Table, Column } from "./Table"
import { ClientAppForm } from "./ClientAppForm"
import { OrgEntity } from "../lib/types/org"

dayjs.extend(relativeTime)

interface ClientAppsResponse {
  count: number
  results: ClientAppEntity[]
}

interface Props {
  organization: OrgEntity
}

export const ApiSettings: React.FC<Props> = ({ organization }) => {
  const modalProps = useDisclosure()
  const theme = useTheme()
  const [page, setPage] = React.useState<number>(1)

  const clientAppsUrl = `client_apps/?organization=${organization.uuid}&page_size=${TABLE_PAGE_SIZE}`

  const getClientApps = React.useCallback(
    (currentPage: number) => fetcher(clientAppsUrl + "&page=" + currentPage),
    [clientAppsUrl],
  )

  const { isLoading, data, refetch } = useQuery<ClientAppsResponse, [string, number]>(
    [clientAppsUrl, page],
    () => getClientApps(page),
  )

  const clientApps = data?.results || []

  return (
    <>
      <Flex justify="space-between" align="flex-end" mb={4}>
        <Heading size="md" as="h4" color="gray.700">
          API Access
        </Heading>
        <Button leftIcon={<HiOutlinePlus />} onClick={modalProps.onOpen} variant="outline">
          Create Account
        </Button>
      </Flex>
      <Flex align="center" border="1px solid" borderColor="gray.100" p={2} mb={4} borderRadius="lg">
        <Box as={RiInformationLine} w="35px" mx={2} />
        <Text ml={1} fontSize="0.8rem">
          Create and delete client accounts to allow your applications and services to obtain credentials that
          grant you access to one or more resources within the Noa API. Each account is associated with a key
          pair, generated when you first create the account. Note that the private key will be made available
          for download only at this time, and will not be kept anywhere within our system, hence it is your
          responsibility to keep and secure it, while operational.
        </Text>
      </Flex>
      <Table
        isLoading={isLoading}
        data={clientApps}
        count={data?.count || 0}
        noData="No client accounts"
        page={page}
        pageSize={TABLE_PAGE_SIZE}
        setPage={setPage}
      >
        <Column<ClientAppEntity>
          header="NAME"
          row={(clientApp) => <Text fontWeight="bold">{clientApp.label || clientApp.name}</Text>}
        />
        <Column<ClientAppEntity> header="ACCOUNT ID" row={(clientApp) => clientApp.name} />
        <Column<ClientAppEntity>
          maxW="150px"
          header="SCOPES"
          row={(clientApp) => (
            <Flex direction="column">
              {clientApp.scopes.map((scope) => (
                <Flex key={scope}>
                  <Text>{scope.split(":")[0]}:</Text>
                  <Text color={theme.colors.gray[500]}>{scope.split(":")[1]}</Text>
                </Flex>
              ))}
            </Flex>
          )}
        />
        <Column<ClientAppEntity>
          maxW="150px"
          header="CREATION DATE"
          row={(clientApp) => dayjs(clientApp.created).fromNow()}
        />
        <Column<ClientAppEntity>
          maxW="50px"
          header=""
          row={(clientApp) => <DeleteClientApp clientApp={clientApp} refetch={refetch} />}
        />
      </Table>
      <Modal {...modalProps} isCentered>
        <ModalOverlay zIndex={100}>
          <ModalContent>
            <ModalHeader>Create Client Application Account</ModalHeader>
            <ModalCloseButton />
            <ModalBody p={6} pt={0}>
              <ClientAppForm organization={organization} onClose={modalProps.onClose} refetch={refetch} />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  )
}

interface DeleteClientAppProps {
  clientApp: ClientAppEntity
  refetch: () => void
}

const DeleteClientApp: React.FC<DeleteClientAppProps> = (props) => {
  const toast = useToast()

  const { mutateAsync: destroy, isLoading } = useMutation<undefined, unknown>(() =>
    fetcher(`client_apps/${props.clientApp.uuid}/`, { method: "delete" }),
  )

  const handleDeleteClientApp = async () => {
    try {
      const res = await destroy()
      if (!res) {
        props.refetch()
        toast({ title: "Successfully deleted client application account", status: "success" })
      } else {
        toast({ title: "Error deleting client application account", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  return (
    <Popover placement="left">
      <PopoverTrigger>
        <IconButton
          size="sm"
          ml={2}
          variant="ghost"
          colorScheme="red"
          aria-label="Delete zone"
          icon={<BiTrash size="15px" />}
        />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="bold">Delete Client Application Account</PopoverHeader>
        <PopoverBody>
          {/* Dirty <br> hack because of unsolved word break issue */}
          <Text mb={2}>
            Are you sure you want to delete this client<br></br>application account?
          </Text>
          <Flex justify="flex-end">
            <Button colorScheme="red" onClick={handleDeleteClientApp} isLoading={isLoading}>
              Delete
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
